// Shared bits for the entity drawers: drawer shell + stats grid, empty/
// error hints, label + name-map resolvers, and the parallel-fetch hook.
import React, { useEffect, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useDispatch } from 'react-redux'

import { Drawer } from '@/components/drawer/Drawer'
import { Pill } from '@/components/pills/Pill'
import { fetchingAPI, apiService } from '@/api'
import { CHART } from '@/components/charts/chartPalette'

// ---- entityDrawerHints ----
export const EmptyHint = ({ children }) => <div className="entity-drawer-empty">{children}</div>

export const ErrorHint = ({ children }) => <div className="entity-drawer-error">{children}</div>

// ---- EntityDrawerShell ----
// Common chrome for the three entity drawers: wide auto-trigger Drawer,
// title header with the entity-type pill, and the body wrapper.
export const EntityDrawerShell = ({ title, typeLabel, subtitle, onClose, children }) => (
  <Drawer
    autoTrigger
    wide
    title={
      <div className="entity-drawer-title">
        <h2>{title}</h2>
        <div className="entity-drawer-subtitle">
          <Pill informative small bold>
            {typeLabel}
          </Pill>
          {subtitle}
        </div>
      </div>
    }
    onClose={onClose}
  >
    <div className="entity-drawer-body">{children}</div>
  </Drawer>
)

// Headline stat tiles (agent + scorecard drawers).
export const EntityDrawerStats = ({ stats }) => (
  <div className="entity-drawer-stats">
    {stats.map(({ label, value }) => (
      <div key={label} className="entity-drawer-stat">
        <div className="label">{label}</div>
        <div className="value">{value}</div>
      </div>
    ))}
  </div>
)

// Base ScoredCallsBrowser scope from the dashboard filters; drawers
// override `agents`/`scorecards` to pin their own entity.
// The filter dimensions the scored-calls browser scopes by. Fields absent
// from this list are not forwarded, so on those the list stays broader
// than the charts above it (playbooks, dispositions, isWin,
// voipCampaigns) -- a known remaining gap, tracked on TREN-518.
//
// `scoredStatus` MUST stay in the list. For most filters a missing param
// does mean "unset", but scored_status defaults to 'scored' server-side
// and is only skipped when the value is literally "any" -- so dropping it
// does not widen the list, it narrows it to fully-scored calls. That put
// the two halves of a drawer on different populations: "failed 40 times"
// above an empty call list, for any org whose calls are partially scored.
export const buildDrawerScopeFilters = (filters, overrides) => ({
  startDate: filters?.startDate,
  endDate: filters?.endDate,
  scorecards: filters?.scorecards,
  agents: filters?.agents,
  tags: filters?.tags,
  callType: filters?.callType,
  scoredStatus: filters?.scoredStatus,
  ...overrides,
})

// "Score Over Time" line option shared by the agent + scorecard drawers.
export const buildScoreTrendOption = (trendRows) => ({
  tooltip: { trigger: 'axis' },
  grid: { left: 40, right: 16, top: 24, bottom: 32 },
  xAxis: { type: 'category', data: trendRows.map((row) => row.day), axisLabel: { fontSize: 10 } },
  yAxis: { type: 'value', min: 0, max: 100, name: 'QA Score' },
  series: [
    {
      type: 'line',
      data: trendRows.map((row) => parseFloat(row['QA Score'] || 0)),
      smooth: true,
      itemStyle: { color: CHART.BRAND },
      areaStyle: { color: CHART.BRAND, opacity: 0.15 },
    },
  ],
})

// FailedCriteriaResponse[] -> sorted failing-percent bar rows, worst first.
export const mapFailingCriteriaRows = (rows, limit) =>
  (rows || [])
    .map((row) => ({
      name: row.criteria_name || row.name || 'Unknown',
      value: parseFloat(row.failing_percent || row.percent || 0),
      row,
    }))
    .sort((rowA, rowB) => rowB.value - rowA.value)
    .slice(0, limit)

// ---- entityResolvers ----
// Display-label resolvers for the widgets' click payloads, which carry
// entity names under varying field names.

export const getAgentLabel = (payload) => {
  if (!payload) {
    return 'Agent'
  }
  const joined = [payload.first_name, payload.last_name].filter(Boolean).join(' ')
  return (
    payload.full_name ||
    payload.name ||
    joined ||
    null ||
    (payload.user_id ? `User ${payload.user_id}` : 'Agent')
  )
}

export const getCriterionLabel = (payload) => payload?.name || payload?.criteria_name || 'Criterion'

// Canonical scorecard-name fields first, then the redux sid->name map.
// Never returns a company/org name (prior stale-field bug).
export const getScorecardLabel = (payload, scorecardNameById) => {
  if (!payload) {
    return 'Scorecard'
  }
  const named =
    payload.scorecard_config_name || payload.config_name || payload.scorecard_name || payload.name
  if (named) {
    return named
  }
  const sid = payload.scorecard_config_sid || payload.sid
  if (sid && scorecardNameById && scorecardNameById[sid]) {
    return scorecardNameById[sid]
  }
  return 'Scorecard'
}

// {user_id -> "First Last"} lookup from redux `data.agents`; option
// shape has drifted across endpoints, so handle the variations.
export const buildAgentNameMap = (agents) => {
  if (!Array.isArray(agents)) {
    return {}
  }
  const map = {}
  agents.forEach((agent) => {
    const id = agent?.value ?? agent?.user_id ?? agent?.id
    if (isNil(id)) {
      return
    }
    const name =
      agent?.label ||
      agent?.full_name ||
      [agent?.first_name, agent?.last_name].filter(Boolean).join(' ') ||
      null
    if (name) {
      map[id] = name
    }
  })
  return map
}

export const buildScorecardNameMap = (scorecards) => {
  if (!Array.isArray(scorecards)) {
    return {}
  }
  const map = {}
  scorecards.forEach((scorecard) => {
    const sid = scorecard?.value ?? scorecard?.sid ?? scorecard?.id
    const name = scorecard?.label ?? scorecard?.name
    if (sid && name) {
      map[sid] = name
    }
  })
  return map
}

// ---- useDrawerFetches ----
// Runs N GET fetches in parallel: `data[i]` matches the URL at index i
// (null URL or failed fetch -> null); `error` only when ALL failed.
export const useDrawerFetches = (...urls) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const active = urls.filter(Boolean)
    if (isEmpty(active)) {
      setData(urls.map(() => null))
      setLoading(false)
      setError(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(false)
    Promise.all(
      urls.map((url) =>
        url ? fetchingAPI(url, 'GET', dispatch).catch(() => null) : Promise.resolve(null)
      )
    )
      .then((results) => {
        if (cancelled) {
          return
        }
        const failures = results.filter((result, index) => urls[index] && isNil(result)).length
        setData(results)
        setError(failures === active.length)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
    // Spread the rest array so React compares URLs individually;
    // callers don't need to memoize.
  }, urls)

  return { data, loading, error }
}

// The agent + scorecard drawers fetch the same two ClickHouse
// aggregates (score-by-day + failing criteria), differing only in params.
export const useDrawerAggregates = (params) =>
  useDrawerFetches(
    params &&
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple/by_date?${params}`,
    params &&
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${params}`
  )
